import {
  type GetSingleQuestionPropertiesByStatusAndLocationQuery,
  type Tag,
} from '@exam-management/graphql';

import { type DashboardQBankStructureLevelItemWithLevel } from '../types';

import { findParentIds, pushHierarchy } from './dashboards';

type SingleQuestionPropertiesData =
  GetSingleQuestionPropertiesByStatusAndLocationQuery['qctGetSingleQuestionPropertiesByStatusAndLocation'][0];

function sortedQBankStructureLevelItems(
  data: DashboardQBankStructureLevelItemWithLevel[],
): DashboardQBankStructureLevelItemWithLevel[] {
  const sortedData: DashboardQBankStructureLevelItemWithLevel[] = [];

  const itemsByParentId = new Map<string | null, DashboardQBankStructureLevelItemWithLevel[]>();
  for (const item of data) {
    const parentId = item.parentLevelItemId ?? null;
    const children = itemsByParentId.get(parentId);
    if (children) {
      children.push(item);
    } else {
      itemsByParentId.set(parentId, [item]);
    }
  }

  pushHierarchy(null, itemsByParentId, sortedData);

  return sortedData;
}

function getTotalQuestionByLevelItemId(
  singleQuestionPropertiesByLocation: SingleQuestionPropertiesData | undefined,
  difficultyCategoryId: string,
  calculationCategoryId: string,
) {
  if (!singleQuestionPropertiesByLocation) return null;
  const questionProperties = singleQuestionPropertiesByLocation.questionProperties ?? [];
  const totalQuestions = questionProperties.find((property) => {
    const hasDifficultyTag = property.tags.some((tag) => tag.id === difficultyCategoryId);

    const hasCalculationTag = property.tags.some((tag) => tag.id === calculationCategoryId);
    return hasDifficultyTag && hasCalculationTag;
  });
  return totalQuestions?.totalQuestions ?? null;
}

export function formatDataToQBankStructureSingleQuestionDashboardTable(input: {
  qBankStructureLevelItems: DashboardQBankStructureLevelItemWithLevel[];
  singleQuestionPropertiesData: SingleQuestionPropertiesData[];
  difficultyTags: Tag[];
  calculationTags: Tag[];
}) {
  const {
    qBankStructureLevelItems,
    singleQuestionPropertiesData,
    difficultyTags,
    calculationTags,
  } = input;
  const sortedQBankStructure = sortedQBankStructureLevelItems(qBankStructureLevelItems);
  const difficultyMap = {
    hard: difficultyTags.find((t) => t.name === 'ยาก')?.id ?? '',
    medium: difficultyTags.find((t) => t.name === 'กลาง')?.id ?? '',
    easy: difficultyTags.find((t) => t.name === 'ง่าย')?.id ?? '',
  };

  const calculationMap = {
    calculate: calculationTags.find((t) => t.name === 'คำนวณ')?.id ?? '',
    nonCalculate: calculationTags.find((t) => t.name === 'ไม่คำนวณ')?.id ?? '',
  };

  const formattedData = sortedQBankStructure.map((levelItem) => {
    const singleQuestionPropertiesForLevelItem = singleQuestionPropertiesData.find(
      (question) => question.locationId === levelItem.id,
    );

    const parentIds = findParentIds(qBankStructureLevelItems, levelItem.parentLevelItemId);

    return {
      id: levelItem.id,
      levelName: levelItem.tag?.name,
      parentId: levelItem.parentLevelItemId,
      level: levelItem.qBankStructureLevel.category,
      order: levelItem.order,
      parentIds,
      calculate: {
        hardQuestions: getTotalQuestionByLevelItemId(
          singleQuestionPropertiesForLevelItem,
          difficultyMap.hard,
          calculationMap.calculate,
        ),
        mediumQuestions: getTotalQuestionByLevelItemId(
          singleQuestionPropertiesForLevelItem,
          difficultyMap.medium,
          calculationMap.calculate,
        ),
        easyQuestions: getTotalQuestionByLevelItemId(
          singleQuestionPropertiesForLevelItem,
          difficultyMap.easy,
          calculationMap.calculate,
        ),
      },
      nonCalculate: {
        hardQuestions: getTotalQuestionByLevelItemId(
          singleQuestionPropertiesForLevelItem,
          difficultyMap.hard,
          calculationMap.nonCalculate,
        ),
        mediumQuestions: getTotalQuestionByLevelItemId(
          singleQuestionPropertiesForLevelItem,
          difficultyMap.medium,
          calculationMap.nonCalculate,
        ),
        easyQuestions: getTotalQuestionByLevelItemId(
          singleQuestionPropertiesForLevelItem,
          difficultyMap.easy,
          calculationMap.nonCalculate,
        ),
      },
    };
  });
  return formattedData;
}
